// Loads the Razorpay Checkout SDK on demand. Called just before opening
// the payment modal so pages that never trigger a payment don't ship the
// third-party script. Safe to call repeatedly — after the first successful
// load the promise resolves synchronously with `true`.

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
const LOAD_TIMEOUT_MS = 15000; // ad-blockers / offline: fail fast after 15s

let _loadPromise = null;

export const loadRazorpayCheckout = () => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise((resolve) => {
    // If an earlier attempt injected the script but it hasn't finished
    // downloading yet, reuse that tag rather than duplicating it.
    let script = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = RAZORPAY_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    const timer = setTimeout(() => {
      cleanup();
      // Allow a future retry to re-attempt loading.
      _loadPromise = null;
      resolve(false);
    }, LOAD_TIMEOUT_MS);

    const onLoad = () => {
      cleanup();
      resolve(!!window.Razorpay);
    };
    const onError = () => {
      cleanup();
      _loadPromise = null;
      resolve(false);
    };
    const cleanup = () => {
      clearTimeout(timer);
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
  });

  return _loadPromise;
};

// Standard user-facing error copy — surfaced from every payment button when
// the checkout SDK fails to load. Match the exact wording the product spec
// asked for so it reads consistently across pages.
export const RAZORPAY_LOAD_ERROR =
  'Payment could not be initialised. Please check your connection and try again.';
